import { computed, ref } from 'vue';
import ApiService from '../services/ApiService.js';
import OfflineSubmissionService from '../services/OfflineSubmissionService.js';
import { isConnected } from './useConnectivityStatus';
import { pendingQueueCount, refreshPendingQueueCount } from './useOfflineQueue';

const syncStatus = ref('idle');
const lastSyncAt = ref(null);
const syncError = ref('');


let initialized = false;
let intervalId = null;

const RETRY_DELAYS_MS = [5000, 30000, 120000, 600000];

const getRecaptchaToken = async () => {
  const siteKey = window.AppConfig?.recaptchaSiteKey;
  const grecaptcha = window.grecaptcha;

  if (!siteKey || !grecaptcha?.ready || !grecaptcha?.execute) {
    return null;
  }

  await new Promise((resolve) => {
    grecaptcha.ready(resolve);
  });

  try {
    return await grecaptcha.execute(siteKey, { action: 'submit' });
  } catch {
    return null;
  }
};

const buildSyncPayload = async (payload, endpoint) => {
  const syncPayload = { ...payload };

  if (endpoint === '/api/lead-submissions') {
    const token = await getRecaptchaToken();
    if (!token) {
      throw new Error('reCAPTCHA token unavailable for secure replay.');
    }

    syncPayload['g-recaptcha-response'] = token;
  }

  return syncPayload;
};

const getRetryDelay = (retryCount) => {
  if (retryCount <= 0) return 0;
  return RETRY_DELAYS_MS[Math.min(retryCount - 1, RETRY_DELAYS_MS.length - 1)];
};

const canRetryNow = (submission, force = false) => {
  if (force) return true;
  if (!submission.last_attempt_at) return true;

  const elapsed = Date.now() - new Date(submission.last_attempt_at).getTime();
  return elapsed >= getRetryDelay(submission.retry_count);
};

const shouldRemoveAsDuplicate = (error) => {
  const status = error?.response?.status;
  return status === 409;
};

const syncPendingSubmissions = async ({ force = false } = {}) => {
  if (!isConnected.value) {
    syncStatus.value = 'queued';
    await refreshPendingQueueCount();
    return;
  }

  const pending = await OfflineSubmissionService.getPendingSubmissions();

  if (!pending.length) {
    syncStatus.value = 'synced';
    lastSyncAt.value = new Date().toISOString();
    await refreshPendingQueueCount();
    return;
  }

  syncStatus.value = 'syncing';
  syncError.value = '';

  for (const submission of pending) {
    if (!canRetryNow(submission, force)) {
      continue;
    }

    try {
      await OfflineSubmissionService.markSyncing(submission.id);

      const syncPayload = await buildSyncPayload(submission.payload, submission.endpoint);

      await ApiService.post(submission.endpoint, syncPayload, {
        headers: {
          'X-Submission-ID': submission.submission_id,
        },
      });

      await OfflineSubmissionService.removeSubmission(submission.id);
    } catch (error) {
      if (shouldRemoveAsDuplicate(error)) {
        await OfflineSubmissionService.removeSubmission(submission.id);
        continue;
      }

      const nextRetryCount = Number(submission.retry_count || 0) + 1;
      await OfflineSubmissionService.markAttemptFailure(submission.id, nextRetryCount);
      syncError.value = error?.response?.data?.message || error?.message || 'Unable to sync some submissions.';
    }
  }

  await refreshPendingQueueCount();
  const failedCount = await OfflineSubmissionService.getFailedCount();
  if (failedCount > 0) {
    syncStatus.value = 'failed';
  } else {
    syncStatus.value = pendingQueueCount.value > 0 ? 'queued' : 'synced';
  }
  lastSyncAt.value = new Date().toISOString();
};

export const initializeSyncQueue = async () => {
  if (initialized) return;
  initialized = true;

  await refreshPendingQueueCount();
  await syncPendingSubmissions({ force: true });

  window.addEventListener('online', () => {
    syncPendingSubmissions({ force: true });
  });

  window.addEventListener('app:connectivity-change', (event) => {
    if (event?.detail?.isConnected) {
      syncPendingSubmissions({ force: true });
    }
  });

  intervalId = window.setInterval(() => {
    syncPendingSubmissions();
  }, 30000);
};

const stopSyncQueue = () => {
  if (intervalId) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
};

export function useSyncQueue() {
  return {
    syncStatus: computed(() => syncStatus.value),
    pendingQueueCount: computed(() => pendingQueueCount.value),
    lastSyncAt: computed(() => lastSyncAt.value),
    syncError: computed(() => syncError.value),
    initializeSyncQueue,
    syncPendingSubmissions,
    stopSyncQueue,
  };
}
